import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type Brand, useAuth } from '@/lib/auth';
import { useStaff } from '@/lib/staff';
import { useTheme } from '@/lib/theme';
import {
  Briefcase,
  Building2,
  Check,
  ChevronsUpDown,
  CreditCard,
  HelpCircle,
  KeyRound,
  LogOut,
  MoonStar,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Tag,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface UserMenuProps {
  /** Compact (icon-only) trigger for tight headers. */
  compact?: boolean;
}

function initials(input: string): string {
  const parts = input
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function brandBreadcrumb(brand: Brand | undefined, all: Brand[]): string {
  if (!brand) return '';
  if (brand.kind === 'brand' && brand.parentBrandId) {
    const parent = all.find((b) => b.brandId === brand.parentBrandId);
    if (parent) return `${parent.name} › ${brand.name}`;
  }
  return brand.name;
}

function BrandRow({
  brand,
  active,
  onSelect,
  showOrigin = false,
  indent = false,
}: {
  brand: Brand;
  active: boolean;
  onSelect: () => void;
  showOrigin?: boolean;
  indent?: boolean;
}) {
  return (
    <DropdownMenuItem onClick={onSelect} className="flex items-center gap-2">
      <Check className={`size-3.5 ${active ? 'text-brand' : 'text-transparent'}`} />
      {indent ? <span className="text-xs text-muted-foreground">└─</span> : null}
      <span className="flex-1 truncate">{brand.name}</span>
      {showOrigin && brand.origin !== 'direct' ? (
        <span className="text-[10px] text-muted-foreground">
          {brand.origin === 'inherited' ? 'inherited' : 'platform'}
        </span>
      ) : null}
    </DropdownMenuItem>
  );
}

export function UserMenu({ compact = false }: UserMenuProps) {
  const { me, signOut, switchBrand } = useAuth();
  const { isPlatformAdmin } = useStaff();
  const [theme, toggleTheme] = useTheme();
  const navigate = useNavigate();

  if (!me?.user) return null;
  const { user, brands, activeBrandId } = me;
  const displayName = user.name ?? user.email.replace(/@.*/, '');
  const activeBrand = brands.find((b) => b.brandId === activeBrandId);

  const platform = brands.filter((b) => b.kind === 'platform');
  const agencies = brands.filter((b) => b.kind === 'agency');
  const brandsByParent = new Map<string, Brand[]>();
  const orphanBrands: Brand[] = [];
  for (const b of brands) {
    if (b.kind !== 'brand') continue;
    if (b.parentBrandId && agencies.some((a) => a.brandId === b.parentBrandId)) {
      const list = brandsByParent.get(b.parentBrandId) ?? [];
      list.push(b);
      brandsByParent.set(b.parentBrandId, list);
    } else {
      orphanBrands.push(b);
    }
  }

  const breadcrumb = brandBreadcrumb(activeBrand, brands);

  // Avatar initials are text-fg, not text-brand. Measured 2026-09-02 in a real
  // browser on the served build: the brand-soft chip is hue 220 and the brand
  // ink hue 221 -- a ONE degree gap, so ink and ground are the same colour and
  // only their lightness differs. That pairing scores 5.02:1 and PASSES AA,
  // which is why a ratio-only check called it fixed while Jay still could not
  // read it: "the color on the profile switcher is mostly the same, super hard
  // to read". At 10-12px a same-hue pair needs far more than the 4.5 floor.
  // --fg gives 17.48:1 dark and 16.58:1 light, and the tint is unchanged.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <Button variant="ghost" size="icon" className="rounded-full" aria-label="Account menu">
            <Avatar className="size-8">
              <AvatarFallback className="bg-brand-soft text-xs font-medium text-fg">
                {initials(displayName)}
              </AvatarFallback>
            </Avatar>
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="h-auto gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-2"
          >
            <Avatar className="size-7">
              <AvatarFallback className="bg-brand-soft text-[10px] font-medium text-fg">
                {initials(displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="hidden flex-col items-start text-left sm:flex">
              <span className="text-xs font-medium text-fg">{displayName}</span>
              {breadcrumb ? <span className="text-[10px] text-fg-soft">{breadcrumb}</span> : null}
            </div>
            <ChevronsUpDown className="size-3.5 text-muted-foreground" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex flex-col">
          <span className="text-sm font-medium text-fg">{displayName}</span>
          <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {brands.length > 1 ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Building2 className="size-4" />
              Switch workspace
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64">
              {platform.length > 0 ? (
                <>
                  <DropdownMenuLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles className="size-3" /> Platform
                    </span>
                  </DropdownMenuLabel>
                  {platform.map((t) => (
                    <BrandRow
                      key={t.brandId}
                      brand={t}
                      active={t.brandId === activeBrandId}
                      onSelect={() => void switchBrand(t.brandId)}
                    />
                  ))}
                </>
              ) : null}
              {agencies.length > 0 ? (
                <>
                  {platform.length > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase className="size-3" /> Agencies
                    </span>
                  </DropdownMenuLabel>
                  {agencies.map((agency) => (
                    <div key={agency.brandId}>
                      <BrandRow
                        brand={agency}
                        active={agency.brandId === activeBrandId}
                        onSelect={() => void switchBrand(agency.brandId)}
                      />
                      {(brandsByParent.get(agency.brandId) ?? []).map((brand) => (
                        <BrandRow
                          key={brand.brandId}
                          brand={brand}
                          active={brand.brandId === activeBrandId}
                          onSelect={() => void switchBrand(brand.brandId)}
                          showOrigin
                          indent
                        />
                      ))}
                    </div>
                  ))}
                </>
              ) : null}
              {orphanBrands.length > 0 ? (
                <>
                  {platform.length > 0 || agencies.length > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Tag className="size-3" /> Brands
                    </span>
                  </DropdownMenuLabel>
                  {orphanBrands.map((t) => (
                    <BrandRow
                      key={t.brandId}
                      brand={t}
                      active={t.brandId === activeBrandId}
                      onSelect={() => void switchBrand(t.brandId)}
                      showOrigin
                    />
                  ))}
                </>
              ) : null}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate('/set-password')}>
            <KeyRound className="size-4" />
            {user.hasPassword ? 'Change password' : 'Set password'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={toggleTheme}>
            {theme === 'dark' ? (
              <>
                <Sun className="size-4" />
                Light theme
              </>
            ) : (
              <>
                <MoonStar className="size-4" />
                Dark theme
              </>
            )}
          </DropdownMenuItem>
          {isPlatformAdmin ? (
            <DropdownMenuItem asChild>
              <Link to="/admin">
                <ShieldCheck className="size-4" />
                Admin console
              </Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem asChild>
            <Link to="/billing">
              <CreditCard className="size-4" />
              Billing
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account">
              <Settings className="size-4" />
              Account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/support">
              <HelpCircle className="size-4" />
              Help &amp; docs
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void signOut()} className="text-danger focus:text-danger">
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
